import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, withCtx as _withCtx, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeStyle as _normalizeStyle, unref as _unref, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "health-calendar" }
const _hoisted_2 = { class: "month-nav px-4 py-2" }
const _hoisted_3 = { class: "d-flex align-center justify-space-between" }
const _hoisted_4 = { class: "d-flex align-center" }
const _hoisted_5 = { class: "d-flex flex-column" }
const _hoisted_6 = { class: "month-grid" }
const _hoisted_7 = { class: "calendar-days px-4 py-2" }
const _hoisted_8 = { class: "d-flex justify-space-between" }
const _hoisted_9 = { class: "d-flex justify-space-between mt-4" }
const _hoisted_10 = ["onClick"]
const _hoisted_11 = { class: "selected-date text-center py-2" }
const _hoisted_12 = { class: "text-caption text-grey-darken-1" }
const _hoisted_13 = { class: "entries-list px-4 pt-4" }
const _hoisted_14 = { class: "d-flex align-center" }
const _hoisted_15 = { class: "flex-grow-1" }
const _hoisted_16 = {
  key: 0,
  class: "d-flex align-center mt-1"
}
const _hoisted_17 = { class: "ml-1 truncate" }
const _hoisted_18 = {
  key: 1,
  class: "d-flex align-center mt-1"
}
const _hoisted_19 = {
  style: `color: white`,
  class: "font-weight-bold"
}
const _hoisted_20 = {
  class: "ml-1 truncate",
  style: `color: white`
}
const _hoisted_21 = {
  key: 0,
  class: "ml-2",
  style: `color: white`
}
const _hoisted_22 = { class: "entry-type-grid" }
const _hoisted_23 = { key: 0 }
const _hoisted_24 = { class: "mb-4" }
const _hoisted_25 = { class: "text-body-1" }
const _hoisted_26 = { class: "mb-4" }
const _hoisted_27 = { class: "text-body-1" }
const _hoisted_28 = { class: "mb-4" }
const _hoisted_29 = { class: "text-body-1" }
const _hoisted_30 = {
  key: 0,
  class: "mb-4"
}
const _hoisted_31 = { class: "text-body-1" }
const _hoisted_32 = {
  key: 1,
  class: "mb-4"
}
const _hoisted_33 = ["src"]

import { ref, computed, onMounted, watch, inject } from 'vue';
import ButtonFixed from "@/components/custom/ButtonFixed.vue";
import DiaryEntryForm from "@/components/HealthSuite/DiaryEntryForm.vue";
import PlannerEntryForm from "@/components/HealthSuite/PlannerEntryForm.vue";
import DrinkIcon from "@/components/Icons/DrinkIcon.vue";
import FeelIcon from "@/components/Icons/FeelIcon.vue";
import DidIcon from "@/components/Icons/DidIcon.vue";
import ExerciseIcon from "@/components/Icons/ExerciseIcon.vue";
import { useSettingsStore } from "@/api/store/settings";
import { healthDiaryService, type DiaryEntry } from "@/api/services/healthDiary";
import { healthPlannerService, type PlannerEntry } from "@/api/services/healthPlanner";
import { hexToRgba } from "@/tools/design";

interface CalendarEntry {
  id: number;
  time: string;
  description: string;
  icon: string;
  iconColor: string;
  color?: string;
  whiteText?: boolean;
  entry_type?: string;
  ate_content?: string;
  noticed_content?: string;
  practitioner_comment?: string;
  status?: {
    text: string;
    color: string;
    textColor: string;
  };
  image_url?: string;
  // Planner entry specific fields
  name?: string;
  planner_type?: string; // Exercise, Food, Drink, Lifestyle
  planner_status?: 'pending' | 'completed' | 'ignored';
  source: 'diary' | 'planner'; // To distinguish entry types
  is_recurring?: boolean;
  is_virtual_occurrence?: boolean;
  original_entry_id?: number;
}

type RefreshFunction = () => void;

export default /*@__PURE__*/_defineComponent({
  __name: 'HealthCalendarView',
  setup(__props) {

const settingsStore = useSettingsStore();
const showDiaryEntryForm = ref(false);
const showEntryTypeSelector = ref(false);
const selectedEntryType = ref<string | null>(null);

// Date management
const currentDate = new Date();
const selectedDate = ref(new Date());
const weekStartDate = ref(new Date());

// Set initial week start to Monday of current week
const dayOfWeek = currentDate.getDay();
const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
weekStartDate.value.setDate(currentDate.getDate() - daysToSubtract);

// Add refresh calendar functionality
const resetToCurrentDate = () => {
  const today = new Date();
  selectedDate.value = today;
  const mondayOfCurrentWeek = new Date(today);
  const dayOfWeek = today.getDay();
  const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  mondayOfCurrentWeek.setDate(today.getDate() - daysToSubtract);
  weekStartDate.value = mondayOfCurrentWeek;
  fetchEntries();
};

const refreshCalendarRef = inject('refreshCalendar', ref<RefreshFunction>(() => { /* intentionally empty */ }));
refreshCalendarRef.value = resetToCurrentDate;

// Computed properties for date display
const weekDates = computed(() => {
  const dates = [];
  for (let i = 0; i < 7; i++) {
    const date = new Date(weekStartDate.value);
    date.setDate(weekStartDate.value.getDate() + i);
    dates.push(date);
  }
  return dates;
});

const currentMonth = computed(() => {
  return weekStartDate.value.toLocaleString('default', { month: 'long' });
});

const currentYear = computed(() => {
  return weekStartDate.value.getFullYear();
});

const formattedSelectedDate = computed(() => {
  return selectedDate.value.toLocaleDateString('default', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    weekday: 'long'
  });
});

// Date helper functions
const isCurrentDate = (date: Date) => {
  return date.toDateString() === currentDate.toDateString();
};

const isSelectedDate = (date: Date) => {
  return date.toDateString() === selectedDate.value.toDateString();
};

const selectDate = (date: Date) => {
  selectedDate.value = new Date(date);
};

const navigateWeek = (direction: number) => {
  const newDate = new Date(weekStartDate.value);
  newDate.setDate(weekStartDate.value.getDate() + (direction * 7));
  weekStartDate.value = newDate;
};

const entries = ref<CalendarEntry[]>([]);
const isLoading = ref(false);
const error = ref<string | null>(null);

// Helper function to get entry type icon
const getEntryTypeIcon = (entryType?: string) => {
  switch (entryType) {
    case 'ate':
      return 'fa fa-cutlery';
    case 'drank':
      return 'custom-drink';
    case 'slept':
      return 'fa fa-bed';
    case 'event':
      return 'fa fa-calendar';
    default:
      return 'fa fa-cutlery';
  }
};

// Helper function to get planner type icon
const getPlannerTypeIcon = (plannerType?: string) => {
  switch (plannerType) {
    case 'Exercise':
      return 'fa fa-star';
    case 'Food':
      return 'fa fa-cutlery';
    case 'Drink':
      return 'fa fa-glass';
    case 'Lifestyle':
      return 'fa fa-heart';
    default:
      return 'fa fa-calendar';
  }
};

// Helper function to get planner status info
const getPlannerStatusInfo = (status?: string) => {
  switch (status) {
    case 'completed':
      return {
        text: 'Completed',
        color: '#4CAF50',
        textColor: 'white'
      };
    case 'ignored':
      return {
        text: 'Ignored',
        color: '#9E9E9E',
        textColor: 'white'
      };
    case 'pending':
    default:
      return {
        text: 'Pending',
        color: '#FF9800',
        textColor: 'white'
      };
  }
};

// Helper function to get entry type label
const getEntryTypeLabel = (entryType?: string) => {
  switch (entryType) {
    case 'ate':
      return 'I ate';
    case 'drank':
      return 'I drank';
    case 'exercised':
      return 'I exercised';
    case 'did':
      return 'I did';
    case 'slept':
      return 'I slept';
    case 'felt':
      return 'I feel';
    case 'event':
      return 'Event';
    default:
      return 'I ate';
  }
};

const fetchEntries = async () => {
  isLoading.value = true;
  error.value = null;
  
  try {
    const dateString = selectedDate.value.toISOString().split('T')[0];
    
    // Fetch both diary and planner entries concurrently
    const [diaryEntries, plannerEntries] = await Promise.all([
      healthDiaryService.indexWithEvents(dateString),
      healthPlannerService.index(dateString)
    ]);

    // Transform diary entries
    const transformedDiaryEntries: CalendarEntry[] = Array.isArray(diaryEntries) ? diaryEntries
      .filter((entry): entry is DiaryEntry & { id: number } => entry && entry.id !== undefined)
      .map((entry) => ({
        id: entry.id,
        time: new Date(entry.entry_datetime).toLocaleTimeString('default', { hour: '2-digit', minute: '2-digit' }),
        entry_type: entry.entry_type,
        ate_content: entry.ate_content,
        noticed_content: entry.noticed_content,
        practitioner_comment: entry.practitioner_comment,
        description: entry.ate_content || entry.noticed_content || '',
        icon: getEntryTypeIcon(entry.entry_type),
        iconColor: entry.entry_type === 'event' ? settingsStore.settings.buttonColor2 : settingsStore.settings.buttonColor,
        color: 'white',
        image_url: entry.file?.file_path || undefined,
        source: 'diary' as const
      })) : [];

    // Transform planner entries
    const transformedPlannerEntries: CalendarEntry[] = Array.isArray(plannerEntries) ? plannerEntries
      .filter(entry => entry && (entry.id !== undefined || entry.original_entry_id !== undefined))
      .map((entry) => {
        const entryTime = new Date(entry.scheduled_datetime);
        return {
          id: entry.id || entry.original_entry_id, // Use original_entry_id as fallback
          time: entryTime.toLocaleTimeString('default', { hour: '2-digit', minute: '2-digit' }),
          name: entry.name,
          planner_type: entry.type,
          planner_status: entry.status,
          description: entry.description || entry.name || '',
          icon: getPlannerTypeIcon(entry.type),
          iconColor: 'white',
          color: entry.status === 'completed' ? hexToRgba(settingsStore.settings.primaryColor, 0.6) : 
                 entry.status === 'ignored' ? hexToRgba(settingsStore.settings.primaryColor, 0.4) : 
                 settingsStore.settings.primaryColor,
          whiteText: true,
          status: getPlannerStatusInfo(entry.status),
          image_url: entry.file?.file_path || undefined,
          source: 'planner' as const,
          is_recurring: entry.is_recurring,
          is_virtual_occurrence: entry.is_virtual_occurrence,
          original_entry_id: entry.original_entry_id
        };
      }) : [];

    // Combine and sort entries by time
    const allEntries = [...transformedDiaryEntries, ...transformedPlannerEntries].sort((a, b) => {
      const timeA = a.time.split(':').map(Number);
      const timeB = b.time.split(':').map(Number);
      return (timeA[0] * 60 + timeA[1]) - (timeB[0] * 60 + timeB[1]);
    });

    entries.value = allEntries;
  } catch (err: any) {
    console.error('Error fetching entries:', err);
    error.value = 'Failed to load entries. Please try again.';
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  fetchEntries();
});

watch(selectedDate, () => {
  fetchEntries();
});

const showMonthPicker = ref(false);
const tempYear = ref(currentYear.value);
const tempMonth = ref(currentDate.getMonth());
const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

// Generate a list of years (5 years before and after current year)
const yearOptions = computed(() => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let i = currentYear - 5; i <= currentYear + 5; i++) {
    years.push(i);
  }
  return years;
});

const selectMonth = (index: number) => {
  tempMonth.value = index;
};

const applyMonthYear = () => {
  const newDate = new Date(tempYear.value, tempMonth.value, 1);
  // Adjust to start of week
  const dayOfWeek = newDate.getDay();
  const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  newDate.setDate(newDate.getDate() - daysToSubtract);
  weekStartDate.value = newDate;
  showMonthPicker.value = false;
};

// Entry types for the bottom sheet
const entryTypes = [
  { type: 'ate', label: 'I ate', icon: 'fa fa-cutlery' },
  { type: 'drank', label: 'I drank', icon: 'fa fa-glass' },
  { type: 'exercised', label: 'I exercised', icon: 'fa fa-star' },
  { type: 'did', label: 'I did...', icon: 'fa fa-plus' },
  { type: 'slept', label: 'I slept...', icon: 'fa fa-bed' },
  { type: 'felt', label: 'I feel...', icon: 'fa fa-heart' },
  { type: 'event', label: 'Event...', icon: 'fa fa-calendar' }
];

const selectEntryType = (type: string) => {
  selectedEntryType.value = type;
  showEntryTypeSelector.value = false;
  showDiaryEntryForm.value = true;
};



// Computed colors for button styling
const lightButtonColor = computed(() => {
  return hexToRgba(settingsStore.settings.buttonColor, 0.15);
});

const borderButtonColor = computed(() => {
  return hexToRgba(settingsStore.settings.buttonColor, 0.3);
});

const showEntryDetailsPopup = ref(false);
const selectedEntry = ref<DiaryEntry | null>(null);
const showPlannerDetailsPopup = ref(false);
const selectedPlannerEntry = ref<PlannerEntry | null>(null);
const showEventBookingPopup = ref(false);
const selectedEventEntry = ref<CalendarEntry | null>(null);

const showEntryDetails = async (entry: CalendarEntry) => {
  if (entry.source === 'diary') {
    // Check if it's an event entry - show event booking details instead of diary form
    if (entry.entry_type === 'event') {
      selectedEventEntry.value = entry;
      showEventBookingPopup.value = true;
      return;
    }
    
    // Show regular diary entry details
    try {
      const fullEntry = await healthDiaryService.show(entry.id);
      selectedEntry.value = fullEntry;
      showEntryDetailsPopup.value = true;
    } catch (error) {
      console.error('Error fetching entry details:', error);
      // Fallback to the original method if fetch fails
      selectedEntry.value = {
        id: entry.id,
        entry_datetime: new Date(selectedDate.value.toDateString() + ' ' + entry.time).toISOString(),
        entry_type: entry.entry_type,
        ate_content: entry.ate_content,
        noticed_content: entry.noticed_content,
        practitioner_comment: entry.practitioner_comment,
        read_by_practitioner: false
      };
      showEntryDetailsPopup.value = true;
    }
  } else if (entry.source === 'planner') {
    // Show planner entry details
    try {
      // If it's a virtual occurrence, fetch the original entry
      const entryId = entry.is_virtual_occurrence ? entry.original_entry_id : entry.id;
      if (!entryId) {
        throw new Error('No entry ID available');
      }
      const fullEntry = await healthPlannerService.show(entryId);
      selectedPlannerEntry.value = {
        ...fullEntry,
        scheduled_datetime: new Date(selectedDate.value.toDateString() + ' ' + entry.time).toISOString()
      };
      showPlannerDetailsPopup.value = true;
    } catch (error) {
      console.error('Error fetching planner entry details:', error);
    }
  }
};

const togglePlannerStatus = async (entry: CalendarEntry) => {
  if (entry.source !== 'planner' || !entry.planner_status) return;
  
  try {
    let newStatus: 'pending' | 'completed' | 'ignored';
    
    // Cycle through statuses: pending -> completed -> ignored -> pending
    switch (entry.planner_status) {
      case 'pending':
        newStatus = 'completed';
        break;
      case 'completed':
        newStatus = 'ignored';
        break;
      case 'ignored':
        newStatus = 'pending';
        break;
      default:
        newStatus = 'pending';
    }
    
    // Use original entry ID for virtual occurrences
    const entryId = entry.is_virtual_occurrence ? entry.original_entry_id : entry.id;
    if (!entryId) {
      throw new Error('No entry ID available');
    }
    
    // For virtual occurrences, construct the scheduled_datetime from the selected date and time
    let scheduledDatetime: string | undefined;
    if (entry.is_virtual_occurrence) {
      scheduledDatetime = new Date(selectedDate.value.toDateString() + ' ' + entry.time).toISOString();
    }
    
    await healthPlannerService.updateStatus(entryId, newStatus, scheduledDatetime);
    // Refresh entries to reflect the change
    fetchEntries();
  } catch (error) {
    console.error('Error updating planner entry status:', error);
  }
};

const handleEntryUpdated = (entryDateTime: string) => {
  const entryDate = new Date(entryDateTime);
  const entryDateString = entryDate.toDateString();
  const currentSelectedDateString = selectedDate.value.toDateString();
  
  // If the entry date is different from the currently selected date, navigate to it
  if (entryDateString !== currentSelectedDateString) {
    // Update the selected date
    selectedDate.value = new Date(entryDate);
    
    // Calculate the start of the week for the new date (Monday)
    const dayOfWeek = entryDate.getDay();
    const mondayOfWeek = new Date(entryDate);
    mondayOfWeek.setDate(entryDate.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
    
    // Update the week start date to show the correct week
    weekStartDate.value = mondayOfWeek;
  }
  
  // Fetch entries for the (potentially new) selected date
  fetchEntries();
};

const handleEntryCreated = (entryDateTime: string) => {
  const entryDate = new Date(entryDateTime);
  const entryDateString = entryDate.toDateString();
  const currentSelectedDateString = selectedDate.value.toDateString();
  
  // If the entry date is different from the currently selected date, navigate to it
  if (entryDateString !== currentSelectedDateString) {
    // Update the selected date
    selectedDate.value = new Date(entryDate);
    
    // Calculate the start of the week for the new date (Monday)
    const dayOfWeek = entryDate.getDay();
    const mondayOfWeek = new Date(entryDate);
    mondayOfWeek.setDate(entryDate.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
    
    // Update the week start date to show the correct week
    weekStartDate.value = mondayOfWeek;
  }
  
  // Fetch entries for the (potentially new) selected date
  fetchEntries();
};

const handlePlannerStatusUpdated = () => {
  // Refresh entries to reflect the status change
  fetchEntries();
};

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_select = _resolveComponent("v-select")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_v_card_actions = _resolveComponent("v-card-actions")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_dialog = _resolveComponent("v-dialog")!
  const _component_v_alert = _resolveComponent("v-alert")!
  const _component_v_progress_circular = _resolveComponent("v-progress-circular")!
  const _component_v_chip = _resolveComponent("v-chip")!
  const _component_v_bottom_sheet = _resolveComponent("v-bottom-sheet")!
  const _component_v_card_title = _resolveComponent("v-card-title")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("div", _hoisted_4, [
          _createVNode(_component_v_btn, {
            icon: "",
            variant: "text",
            color: "grey-darken-1",
            onClick: _cache[0] || (_cache[0] = ($event: any) => (navigateWeek(-1)))
          }, {
            default: _withCtx(() => [
              _createVNode(_component_v_icon, { icon: "fa-chevron-left" })
            ]),
            _: 1
          }),
          _createElementVNode("span", {
            class: "text-grey-darken-1 text-body-1 font-weight-semibold month-selector",
            onClick: _cache[1] || (_cache[1] = ($event: any) => (showMonthPicker.value = true))
          }, _toDisplayString(currentMonth.value) + " " + _toDisplayString(currentYear.value), 1),
          _createVNode(_component_v_btn, {
            icon: "",
            variant: "text",
            color: "grey-darken-1",
            onClick: _cache[2] || (_cache[2] = ($event: any) => (navigateWeek(1)))
          }, {
            default: _withCtx(() => [
              _createVNode(_component_v_icon, { icon: "fa-chevron-right" })
            ]),
            _: 1
          })
        ])
      ])
    ]),
    _createVNode(_component_v_dialog, {
      modelValue: showMonthPicker.value,
      "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((showMonthPicker).value = $event)),
      width: "300"
    }, {
      default: _withCtx(() => [
        _createVNode(_component_v_card, null, {
          default: _withCtx(() => [
            _createVNode(_component_v_card_text, { class: "pa-4" }, {
              default: _withCtx(() => [
                _createElementVNode("div", _hoisted_5, [
                  _createVNode(_component_v_select, {
                    modelValue: tempYear.value,
                    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((tempYear).value = $event)),
                    items: yearOptions.value,
                    label: "Year",
                    variant: "outlined",
                    density: "compact",
                    class: "mb-4"
                  }, null, 8, ["modelValue", "items"]),
                  _createElementVNode("div", _hoisted_6, [
                    (_openBlock(), _createElementBlock(_Fragment, null, _renderList(monthNames, (month, index) => {
                      return _createVNode(_component_v_btn, {
                        key: month,
                        variant: "text",
                        color: tempMonth.value === index ? 'primary' : '',
                        onClick: ($event: any) => (selectMonth(index)),
                        class: "month-btn"
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(month.slice(0, 3)), 1)
                        ]),
                        _: 2
                      }, 1032, ["color", "onClick"])
                    }), 64))
                  ])
                ])
              ]),
              _: 1
            }),
            _createVNode(_component_v_card_actions, null, {
              default: _withCtx(() => [
                _createVNode(_component_v_spacer),
                _createVNode(_component_v_btn, {
                  color: "grey",
                  variant: "text",
                  onClick: _cache[4] || (_cache[4] = ($event: any) => (showMonthPicker.value = false))
                }, {
                  default: _withCtx(() => [...(_cache[13] || (_cache[13] = [
                    _createTextVNode("Cancel", -1)
                  ]))]),
                  _: 1
                }),
                _createVNode(_component_v_btn, {
                  color: "primary",
                  variant: "text",
                  onClick: applyMonthYear
                }, {
                  default: _withCtx(() => [...(_cache[14] || (_cache[14] = [
                    _createTextVNode("OK", -1)
                  ]))]),
                  _: 1
                })
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["modelValue"]),
    _createElementVNode("div", _hoisted_7, [
      _createElementVNode("div", _hoisted_8, [
        (_openBlock(), _createElementBlock(_Fragment, null, _renderList(['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'], (day) => {
          return _createElementVNode("div", {
            key: day,
            class: _normalizeClass(["day-label text-caption font-weight-medium", {'grey-text': day === 'SAT' || day === 'SUN'}])
          }, _toDisplayString(day), 3)
        }), 64))
      ]),
      _createElementVNode("div", _hoisted_9, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(weekDates.value, (date) => {
          return (_openBlock(), _createElementBlock("div", {
            key: date.getTime(),
            class: _normalizeClass(["date-number text-subtitle-1 font-weight-medium", {
            'active': isSelectedDate(date)
          }]),
            onClick: ($event: any) => (selectDate(date))
          }, _toDisplayString(date.getDate()), 11, _hoisted_10))
        }), 128))
      ])
    ]),
    _createElementVNode("div", _hoisted_11, [
      _createElementVNode("div", _hoisted_12, _toDisplayString(formattedSelectedDate.value), 1)
    ]),
    _createElementVNode("div", _hoisted_13, [
      (error.value)
        ? (_openBlock(), _createBlock(_component_v_alert, {
            key: 0,
            type: "error",
            class: "mb-4"
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(error.value), 1)
            ]),
            _: 1
          }))
        : _createCommentVNode("", true),
      (isLoading.value)
        ? (_openBlock(), _createBlock(_component_v_progress_circular, {
            key: 1,
            indeterminate: "",
            color: "primary",
            class: "ma-4"
          }))
        : (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
            (!entries.value || entries.value.length === 0)
              ? (_openBlock(), _createBlock(_component_v_card, {
                  key: 0,
                  class: "mb-4"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_card_text, { class: "text-center pa-4" }, {
                      default: _withCtx(() => [...(_cache[15] || (_cache[15] = [
                        _createTextVNode(" No diary or planner entries for this date. Click \"New Entry\" to add a diary entry. ", -1)
                      ]))]),
                      _: 1
                    })
                  ]),
                  _: 1
                }))
              : (_openBlock(true), _createElementBlock(_Fragment, { key: 1 }, _renderList(entries.value, (entry) => {
                  return (_openBlock(), _createBlock(_component_v_card, {
                    key: entry.source === 'planner' && entry.is_virtual_occurrence ? `${entry.original_entry_id}-${entry.time}` : entry.id,
                    color: entry.color || 'white',
                    class: _normalizeClass(["mb-4 entry-card", {'white-text': entry.whiteText}]),
                    elevation: "1",
                    rounded: "lg",
                    onClick: ($event: any) => (showEntryDetails(entry))
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_card_text, { class: "pa-4" }, {
                        default: _withCtx(() => [
                          _createElementVNode("div", _hoisted_14, [
                            (entry.entry_type === 'drank')
                              ? (_openBlock(), _createBlock(DrinkIcon, {
                                  key: 0,
                                  color: entry.iconColor,
                                  class: "me-4"
                                }, null, 8, ["color"]))
                              : (entry.entry_type === 'felt')
                                ? (_openBlock(), _createBlock(FeelIcon, {
                                    key: 1,
                                    color: entry.iconColor,
                                    class: "me-4"
                                  }, null, 8, ["color"]))
                                : (entry.entry_type === 'did')
                                  ? (_openBlock(), _createBlock(DidIcon, {
                                      key: 2,
                                      color: entry.iconColor,
                                      class: "me-4"
                                    }, null, 8, ["color"]))
                                  : (entry.entry_type === 'exercised')
                                    ? (_openBlock(), _createBlock(ExerciseIcon, {
                                        key: 3,
                                        color: entry.iconColor,
                                        class: "me-4"
                                      }, null, 8, ["color"]))
                                    : (_openBlock(), _createBlock(_component_v_icon, {
                                        key: 4,
                                        icon: entry.icon,
                                        size: "24",
                                        color: entry.iconColor,
                                        class: "me-4"
                                      }, null, 8, ["icon", "color"])),
                            _createElementVNode("div", _hoisted_15, [
                              _createElementVNode("div", {
                                class: "text-body-2 font-weight-semibold",
                                style: _normalizeStyle(entry.whiteText ? 'color: white' : '')
                              }, _toDisplayString(entry.time), 5),
                              (entry.source === 'diary' && entry.ate_content)
                                ? (_openBlock(), _createElementBlock("div", _hoisted_16, [
                                    (entry.entry_type !== 'event')
                                      ? (_openBlock(), _createElementBlock("span", {
                                          key: 0,
                                          style: _normalizeStyle(`color: ${_unref(settingsStore).settings.buttonColor}`),
                                          class: "font-weight-bold"
                                        }, _toDisplayString(getEntryTypeLabel(entry.entry_type)), 5))
                                      : _createCommentVNode("", true),
                                    _createElementVNode("span", _hoisted_17, _toDisplayString(entry.ate_content), 1)
                                  ]))
                                : _createCommentVNode("", true),
                              (entry.source === 'planner')
                                ? (_openBlock(), _createElementBlock("div", _hoisted_18, [
                                    _createElementVNode("span", _hoisted_19, _toDisplayString(entry.planner_type) + ": ", 1),
                                    _createElementVNode("span", _hoisted_20, _toDisplayString(entry.name), 1),
                                    (entry.is_recurring)
                                      ? (_openBlock(), _createElementBlock("span", _hoisted_21, "(Recurring)"))
                                      : _createCommentVNode("", true)
                                  ]))
                                : _createCommentVNode("", true)
                            ]),
                            _createVNode(_component_v_spacer),
                            (entry.status)
                              ? (_openBlock(), _createBlock(_component_v_chip, {
                                  key: 5,
                                  color: entry.source === 'planner' ? 'white' : entry.status.color,
                                  "text-color": entry.source === 'planner' ? entry.status.color : entry.status.textColor,
                                  size: "small",
                                  class: _normalizeClass(["status-chip", { 'clickable-status': entry.source === 'planner' }]),
                                  onClick: _withModifiers(($event: any) => (entry.source === 'planner' ? togglePlannerStatus(entry) : null), ["stop"])
                                }, {
                                  default: _withCtx(() => [
                                    _createTextVNode(_toDisplayString(entry.status.text), 1)
                                  ]),
                                  _: 2
                                }, 1032, ["color", "text-color", "class", "onClick"]))
                              : _createCommentVNode("", true)
                          ])
                        ]),
                        _: 2
                      }, 1024)
                    ]),
                    _: 2
                  }, 1032, ["color", "class", "onClick"]))
                }), 128))
          ], 64))
    ]),
    _createVNode(ButtonFixed, {
      text: "New Entry",
      "custom-class": "circular-add-button text-white primary-button",
      onClick: _cache[6] || (_cache[6] = ($event: any) => (showEntryTypeSelector.value = true))
    }),
    _createVNode(_component_v_bottom_sheet, {
      modelValue: showEntryTypeSelector.value,
      "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((showEntryTypeSelector).value = $event)),
      scrim: true,
      class: "entry-type-bottom-sheet",
      width: "500",
      location: "bottom"
    }, {
      default: _withCtx(() => [
        _createVNode(_component_v_card, { class: "entry-type-card" }, {
          default: _withCtx(() => [
            _cache[16] || (_cache[16] = _createElementVNode("div", { class: "handle-bar" }, null, -1)),
            _createVNode(_component_v_card_text, { class: "pa-6" }, {
              default: _withCtx(() => [
                _createElementVNode("div", _hoisted_22, [
                  (_openBlock(), _createElementBlock(_Fragment, null, _renderList(entryTypes, (entryType) => {
                    return _createVNode(_component_v_card, {
                      key: entryType.type,
                      class: "entry-type-button",
                      elevation: "0",
                      ripple: true,
                      style: _normalizeStyle({ backgroundColor: lightButtonColor.value, border: `1px solid ${_unref(settingsStore).settings.buttonColor}` }),
                      onClick: ($event: any) => (selectEntryType(entryType.type))
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_card_text, { class: "d-flex align-center pa-4" }, {
                          default: _withCtx(() => [
                            (entryType.type === 'drank')
                              ? (_openBlock(), _createBlock(DrinkIcon, {
                                  key: 0,
                                  color: _unref(settingsStore).settings.buttonColor,
                                  class: "me-2"
                                }, null, 8, ["color"]))
                              : (entryType.type === 'felt')
                                ? (_openBlock(), _createBlock(FeelIcon, {
                                    key: 1,
                                    color: _unref(settingsStore).settings.buttonColor,
                                    class: "me-2"
                                  }, null, 8, ["color"]))
                                : (entryType.type === 'did')
                                  ? (_openBlock(), _createBlock(DidIcon, {
                                      key: 2,
                                      color: _unref(settingsStore).settings.buttonColor,
                                      class: "me-2"
                                    }, null, 8, ["color"]))
                                  : (entryType.type === 'exercised')
                                    ? (_openBlock(), _createBlock(ExerciseIcon, {
                                        key: 3,
                                        color: _unref(settingsStore).settings.buttonColor,
                                        class: "me-2"
                                      }, null, 8, ["color"]))
                                    : (_openBlock(), _createBlock(_component_v_icon, {
                                        key: 4,
                                        icon: entryType.icon,
                                        size: "20",
                                        color: _unref(settingsStore).settings.buttonColor,
                                        class: "me-2"
                                      }, null, 8, ["icon", "color"])),
                            _createElementVNode("span", {
                              class: "entry-type-text",
                              style: _normalizeStyle({ color: _unref(settingsStore).settings.buttonColor })
                            }, _toDisplayString(entryType.label) + "...", 5)
                          ]),
                          _: 2
                        }, 1024)
                      ]),
                      _: 2
                    }, 1032, ["style", "onClick"])
                  }), 64))
                ])
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["modelValue"]),
    _createVNode(DiaryEntryForm, {
      modelValue: showEntryDetailsPopup.value,
      "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((showEntryDetailsPopup).value = $event)),
      "entry-type": selectedEntry.value?.entry_type,
      "existing-entry": selectedEntry.value,
      "is-editing": true,
      onEntryUpdated: handleEntryUpdated
    }, null, 8, ["modelValue", "entry-type", "existing-entry"]),
    _createVNode(PlannerEntryForm, {
      modelValue: showPlannerDetailsPopup.value,
      "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((showPlannerDetailsPopup).value = $event)),
      "planner-entry": selectedPlannerEntry.value,
      onStatusUpdated: handlePlannerStatusUpdated
    }, null, 8, ["modelValue", "planner-entry"]),
    _createVNode(DiaryEntryForm, {
      modelValue: showDiaryEntryForm.value,
      "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((showDiaryEntryForm).value = $event)),
      "entry-type": selectedEntryType.value,
      onEntryCreated: handleEntryCreated
    }, null, 8, ["modelValue", "entry-type"]),
    _createVNode(_component_v_dialog, {
      modelValue: showEventBookingPopup.value,
      "onUpdate:modelValue": _cache[12] || (_cache[12] = ($event: any) => ((showEventBookingPopup).value = $event)),
      "max-width": "500px"
    }, {
      default: _withCtx(() => [
        _createVNode(_component_v_card, { class: "event-booking-card" }, {
          default: _withCtx(() => [
            _createVNode(_component_v_card_title, { class: "text-h6 d-flex align-center" }, {
              default: _withCtx(() => [
                _createVNode(_component_v_icon, {
                  icon: "fa fa-calendar",
                  size: "24",
                  color: _unref(settingsStore).settings.buttonColor2,
                  class: "me-3"
                }, null, 8, ["color"]),
                _cache[17] || (_cache[17] = _createTextVNode(" Event Booking Details ", -1))
              ]),
              _: 1
            }),
            _createVNode(_component_v_card_text, { class: "pa-6" }, {
              default: _withCtx(() => [
                (selectedEventEntry.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_23, [
                      _createElementVNode("div", _hoisted_24, [
                        _createElementVNode("div", {
                          class: "text-subtitle-2 font-weight-semibold mb-1",
                          style: _normalizeStyle(`color: ${_unref(settingsStore).settings.buttonColor2}`)
                        }, " Time ", 4),
                        _createElementVNode("div", _hoisted_25, _toDisplayString(selectedEventEntry.value.time), 1)
                      ]),
                      _createElementVNode("div", _hoisted_26, [
                        _createElementVNode("div", {
                          class: "text-subtitle-2 font-weight-semibold mb-1",
                          style: _normalizeStyle(`color: ${_unref(settingsStore).settings.buttonColor2}`)
                        }, " Event Details ", 4),
                        _createElementVNode("div", _hoisted_27, _toDisplayString(selectedEventEntry.value.ate_content || selectedEventEntry.value.description), 1)
                      ]),
                      _createElementVNode("div", _hoisted_28, [
                        _createElementVNode("div", {
                          class: "text-subtitle-2 font-weight-semibold mb-1",
                          style: _normalizeStyle(`color: ${_unref(settingsStore).settings.buttonColor2}`)
                        }, " Date ", 4),
                        _createElementVNode("div", _hoisted_29, _toDisplayString(formattedSelectedDate.value), 1)
                      ]),
                      (selectedEventEntry.value.noticed_content)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_30, [
                            _createElementVNode("div", {
                              class: "text-subtitle-2 font-weight-semibold mb-1",
                              style: _normalizeStyle(`color: ${_unref(settingsStore).settings.buttonColor2}`)
                            }, " Notes ", 4),
                            _createElementVNode("div", _hoisted_31, _toDisplayString(selectedEventEntry.value.noticed_content), 1)
                          ]))
                        : _createCommentVNode("", true),
                      (selectedEventEntry.value.image_url)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_32, [
                            _createElementVNode("div", {
                              class: "text-subtitle-2 font-weight-semibold mb-1",
                              style: _normalizeStyle(`color: ${_unref(settingsStore).settings.buttonColor2}`)
                            }, " Image ", 4),
                            _createElementVNode("img", {
                              src: selectedEventEntry.value.image_url,
                              alt: "Event image",
                              class: "event-image"
                            }, null, 8, _hoisted_33)
                          ]))
                        : _createCommentVNode("", true)
                    ]))
                  : _createCommentVNode("", true)
              ]),
              _: 1
            }),
            _createVNode(_component_v_card_actions, { class: "pa-4" }, {
              default: _withCtx(() => [
                _createVNode(_component_v_spacer),
                _createVNode(_component_v_btn, {
                  color: "grey",
                  variant: "text",
                  onClick: _cache[11] || (_cache[11] = ($event: any) => (showEventBookingPopup.value = false))
                }, {
                  default: _withCtx(() => [...(_cache[18] || (_cache[18] = [
                    _createTextVNode(" Close ", -1)
                  ]))]),
                  _: 1
                })
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["modelValue"])
  ]))
}
}

})